/**
 * MER-515 part 3 + MER-519: Supabase Auth redirects email callbacks back to the
 * Site URL (the bare domain root, per PLATFORM_STATE) with the outcome in the
 * URL HASH:
 *   - failure:   #error=access_denied&error_code=otp_expired&error_description=...
 *   - recovery:  #access_token=...&type=recovery   (forgot-password link)
 *   - invite:    #access_token=...&type=invite     (admin invite link)
 *   - sign-in:   #access_token=...                 (no type / magiclink)
 * The root used to render ComingSoon and silently swallow ALL of these. MER-515
 * surfaced the error (AuthError); MER-519 routes recovery/invite to the
 * set-password screen so invited users can establish a reusable password.
 *
 * Capture the hash AT MODULE LOAD, before the Supabase client exists.
 * getSupabase() is lazy -- first called from useAuth's effect, AFTER the first
 * render -- and runs with detectSessionInUrl:true, which consumes/clears the
 * URL hash. A later read could miss the callback. Module evaluation happens
 * during bundle load, before any getSupabase() call (nothing creates the client
 * at module top level), so this snapshot is pristine and timing-independent.
 *
 * The snapshot is PAGE-LOAD-SCOPED and explicitly clearable
 * (clearCapturedAuthCallback): SetPassword clears it on success so a return to
 * "/" resolves to the dashboard instead of re-routing back to set-password.
 */

let capturedHash = typeof window !== "undefined" && window.location ? window.location.hash : "";

// MER-523: the prefetch-safe recovery/invite flow puts the one-time credential
// in the QUERY string as `?token_hash=...&type=recovery|invite` (the Supabase
// email template links straight to /set-password instead of the GET-consumable
// /auth/v1/verify endpoint). detectSessionInUrl IGNORES token_hash (it only
// auto-processes an access_token hash or a ?code), so a mail-scanner GET can't
// consume it -- the token is spent only when the page explicitly calls
// verifyOtp() on a deliberate click. Snapshot at module load for the same
// timing reasons as the hash above (and so a later react-router navigation that
// rewrites the URL can't drop it).
let capturedSearch = typeof window !== "undefined" && window.location ? window.location.search : "";

/**
 * Parse a URL hash for a Supabase auth-callback error. Pure + exported so the
 * node:test suite exercises it directly.
 *
 * @param {string | null | undefined} hash
 * @returns {{ error: string | null, code: string | null, description: string | null } | null}
 *   null when the hash carries no auth error (absent, a success token hash, or junk).
 */
export function parseAuthErrorHash(hash) {
  if (typeof hash !== "string") return null;
  // Strip a leading "#" (and a stray "?") before parsing the params.
  const raw = hash.replace(/^[#?]+/, "");
  if (!raw) return null;
  const params = new URLSearchParams(raw);
  const error = params.get("error");
  const code = params.get("error_code");
  const description = params.get("error_description");
  // Mirror Supabase's own callback-error detection: any of error / error_code /
  // error_description marks a failed callback. A success callback carries
  // access_token and none of these -> null, so it flows through to the normal
  // session resolve.
  if (!error && !code && !description) return null;
  // `description` is parsed but intentionally NOT rendered: error_description is
  // attacker-influenceable URL text, so AuthError keys its copy off the bounded
  // `code` instead. Kept here for telemetry / debugging.
  return { error, code, description };
}

/**
 * MER-519: the Supabase callback `type` for a SUCCESSFUL recovery / invite link
 * ("recovery" | "invite"), or null. Returns null when the hash carries an error
 * (an expired link is an AuthError, not a set-password trigger) or any other /
 * absent type (a normal sign-in callback resolves to the dashboard).
 *
 * @param {string | null | undefined} hash
 * @returns {"recovery" | "invite" | null}
 */
export function parseRecoveryOrInvite(hash) {
  if (typeof hash !== "string") return null;
  const raw = hash.replace(/^[#?]+/, "");
  if (!raw) return null;
  const params = new URLSearchParams(raw);
  // An error callback is handled as AuthError, never set-password.
  if (params.get("error") || params.get("error_code")) return null;
  const type = params.get("type");
  return type === "recovery" || type === "invite" ? type : null;
}

/**
 * MER-523: parse a URL query string for the prefetch-safe recovery/invite
 * credential `?token_hash=...&type=recovery|invite`. Pure + exported for the
 * node:test suite.
 *
 * Returns the bits to hand to `supabase.auth.verifyOtp({ token_hash, type })`,
 * or null when there is no token_hash, the type is not recovery/invite, or the
 * query is actually an error callback (handled as AuthError, never verified).
 *
 * @param {string | null | undefined} search
 * @returns {{ tokenHash: string, type: "recovery" | "invite" } | null}
 */
export function parseTokenHashParams(search) {
  if (typeof search !== "string") return null;
  const raw = search.replace(/^[#?]+/, "");
  if (!raw) return null;
  const params = new URLSearchParams(raw);
  // An error callback is an AuthError, never a verify trigger.
  if (params.get("error") || params.get("error_code")) return null;
  const tokenHash = params.get("token_hash");
  const type = params.get("type");
  if (!tokenHash) return null;
  return type === "recovery" || type === "invite" ? { tokenHash, type } : null;
}

/** The auth-callback error present in the URL at page load, or null. */
export function capturedAuthError() {
  return parseAuthErrorHash(capturedHash);
}

/** "recovery" | "invite" if the page was loaded via that callback, else null. */
export function capturedRecoveryOrInvite() {
  return parseRecoveryOrInvite(capturedHash);
}

/**
 * MER-523: the `{ tokenHash, type }` to verify if the page was loaded via the
 * prefetch-safe token_hash recovery/invite link, else null.
 */
export function capturedTokenHashParams() {
  return parseTokenHashParams(capturedSearch);
}

/**
 * MER-519: drop the page-load snapshot once consumed (SetPassword calls this on
 * a successful updateUser), so a subsequent navigation to "/" resolves normally
 * instead of RootResolver re-routing back to /set-password. MER-523: also drops
 * the token_hash query snapshot.
 */
export function clearCapturedAuthCallback() {
  capturedHash = "";
  capturedSearch = "";
}
